import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import TranslatableText from "@/components/TranslatableText";
import MemorialSupportPanel from "@/components/MemorialSupportPanel";

// Product decision (2026-02-28): the "Ways to honour X" payment panel is
// temporarily hidden while pricing is being reworked. Flip
// REACT_APP_ENABLE_MEMORIAL_SUPPORT=1 in .env to bring it back.
const SUPPORT_PANEL_ENABLED = process.env.REACT_APP_ENABLE_MEMORIAL_SUPPORT === "1";
import SmartTextInput from "@/components/SmartTextInput";
import { toast } from "sonner";
import TributeWidget from "@/components/TributeWidget";
import MemorialServicesPanel from "@/components/MemorialServicesPanel";
import CopyLinkIcon from "@/components/CopyLinkIcon";
import TributeHeroStrip from "@/components/TributeHeroStrip";
import MemorialContactAdminModal from "@/components/MemorialContactAdminModal";
import GuestbookWall from "@/components/GuestbookWall";
import FamilyTreeAutoLink from "@/components/FamilyTreeAutoLink";
import AnniversaryOptIn from "@/components/AnniversaryOptIn";
import WeatherOnThisDay from "@/components/WeatherOnThisDay";
import StickyMemorialActionBar from "@/components/StickyMemorialActionBar";
import RelatedMemorialsStrip from "@/components/RelatedMemorialsStrip";
import AiMessageAssist from "@/components/AiMessageAssist";
import MemorialBadges from "@/components/MemorialBadges";
import MemorialBadgeAdmin from "@/components/MemorialBadgeAdmin";
import RelatedTraditionCard from "@/components/RelatedTraditionCard";
import { Flower, Heart, Bell, BellOff, MapPin, Calendar, Award, Users, Video, Newspaper, Church, DollarSign, MessageCircle, Share2, Download, X as XIcon, Copy, Facebook, Sparkles, TreePine, Plus, Palette, RefreshCcw, Check, Printer, Wand2, UserPlus } from "lucide-react";

const REACTION_ICONS = { like: "👍", heart: "❤️", respect: "🙏", thank_you: "🌸" };

export default function CelebrateDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [contribs, setContribs] = useState([]);
  const [tab, setTab] = useState("about");

  // Hash-based tab jump: sticky action bar's "Leave a memory" link
  // (`#leave-a-memory`) auto-switches to the memories tab.
  useEffect(() => {
    const applyHash = () => {
      const hash = (window.location.hash || "").toLowerCase();
      if (hash === "#leave-a-memory") {
        setTab("memories");
        // Give React time to render the panel before scrolling.
        setTimeout(() => {
          const el = document.getElementById("leave-a-memory");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);
  const [newContrib, setNewContrib] = useState({ kind: "guestbook", body: "", author_name: user?.name || "", author_email: user?.email || "" });
  const [shareOpen, setShareOpen] = useState(false);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [contactAdminOpen, setContactAdminOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/memorials/${slug}`);
      setM(data);
      const { data: cd } = await http.get(`/memorials/${data.id}/contributions`);
      setContribs(cd.items || []);
    } catch (e) {
      setM(false);
    }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  // Lazy AI memorial card for imported obituaries — first visitor
  // fires the auto endpoint; subsequent viewers get the cached card.
  useEffect(() => {
    if (!m || !m.id) return;
    if (m.memorial_type !== "external_obituary") return;
    if (m.memorial_card_url) return;
    if (!m.portrait && !(m.portraits && m.portraits[0])) return;
    let cancelled = false;
    http.post(`/memorials/${m.id}/ai-memorial-card/auto`).then((r) => {
      if (cancelled) return;
      if (r.data?.url) setM((prev) => (prev ? { ...prev, memorial_card_url: r.data.url } : prev));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [m?.id, m?.memorial_type, m?.memorial_card_url]);

  if (m === null) return <div className="max-w-3xl mx-auto py-24 text-center" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>;
  if (m === false) return <div className="max-w-3xl mx-auto py-24 text-center"><h2 className="font-display text-3xl">Memorial not found</h2><Link className="mt-6 wofy-btn-primary inline-flex" to="/celebrate">Back to Celebrate a Life</Link></div>;

  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow this memorial"); return; }
    try {
      const { data } = await http.post(`/memorials/${m.id}/${m.is_following ? "unfollow" : "follow"}`);
      setM({ ...m, is_following: data.following, follower_count: data.follower_count });
      toast.success(data.following ? "You'll receive anniversary reminders & updates" : "Unfollowed");
    } catch { toast.error("Action failed"); }
  };

  const submitContrib = async (e) => {
    e.preventDefault();
    if (!newContrib.body.trim()) return;
    try {
      await http.post(`/memorials/${m.id}/contributions`, newContrib);
      toast.success("Thank you — your message is awaiting family approval.");
      setNewContrib({ ...newContrib, body: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const react = async (cid, reaction) => {
    try {
      await http.post(`/memorials/contributions/${cid}/react?reaction=${reaction}`);
      // Optimistic increment
      setContribs((prev) => prev.map((c) => c.id === cid ? { ...c, reactions: { ...(c.reactions || {}), [reaction]: (c.reactions?.[reaction] || 0) + 1 } } : c));
    } catch { toast.error("Sign in to react"); }
  };

  const years = [m.birth_date?.slice(0, 4), m.death_date?.slice(0, 4)].filter(Boolean).join("–");
  const byKind = (k) => contribs.filter((c) => c.kind === k);

  // Canonical share URLs use the production domain regardless of preview host
  // so social crawlers cache the right OG data.
  const canonicalDomain = "https://socialpulse.com";
  const canonicalUrl = `${canonicalDomain}/celebrate/${m.slug}`;
  const cardRelUrl = m.share_card_url || `/api/memorials/${m.slug}/card.jpg`;
  const cardAbsUrl = cardRelUrl.startsWith("http") ? cardRelUrl : `${canonicalDomain}${cardRelUrl}`;
  const ogTitle = `Remembering ${m.full_name} — Social Pulse`;
  const ogDesc = m.ai_biography?.epitaph || m.quote || m.short_bio ||
    `A tribute to ${m.full_name}${years ? ` (${years})` : ""} on Social Pulse.`;
  const ogDescShort = String(ogDesc).slice(0, 240);

  return (
    <div data-testid="celebrate-detail" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescShort} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="Social Pulse" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescShort} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={cardAbsUrl} />
        <meta property="og:image:secure_url" content={cardAbsUrl} />
        <meta property="og:image:type" content={m.share_card_mime || "image/jpeg"} />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="1024" />
        <meta property="og:image:alt" content={`Tribute card for ${m.full_name}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescShort} />
        <meta name="twitter:image" content={cardAbsUrl} />
      </Helmet>

      {/* Sticky action bar — slides in when user scrolls past the hero */}
      <StickyMemorialActionBar
        memorial={m}
        onFollowChange={(nextFollowing, count) => setM((prev) => prev ? ({
          ...prev,
          is_following: nextFollowing,
          follower_count: typeof count === "number" ? count : prev.follower_count,
        }) : prev)}
      />

      {/* Cover */}
      {m.cover_image && (
        <div className="w-full aspect-[3/1] overflow-hidden rounded-2xl">
          <img src={m.cover_image} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      {/* Hero */}
      <div className={`grid md:grid-cols-3 gap-8 ${m.cover_image ? "-mt-14 relative z-10" : "mt-4"}`}>
        <div className="md:col-span-1">
          <div className="aspect-square rounded-2xl overflow-hidden border-4 relative" style={{ borderColor: "var(--wofy-paper)", background: "var(--wofy-surface)" }} data-testid="mem-hero-image">
            {(m.memorial_card_url || m.portrait) ? (
              <img src={m.memorial_card_url || m.portrait} alt={m.full_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Flower className="w-16 h-16 opacity-30" /></div>
            )}
            <CopyLinkIcon
              url={`/celebrate/${m.slug || m.id}`}
              position="tr"
              label="Copy memorial link"
              testId="mem-hero-copy-link"
            />
            {/* Decorative flowers around the portrait — rendered only
                when the memorial's `decorations.flowers` was curated by
                an editor (e.g. Indian Veterans get marigold + rose
                petals). Purely cosmetic; never blocks the image click. */}
            {Array.isArray(m?.decorations?.flowers) && m.decorations.flowers.length > 0 && (
              <div
                className="pointer-events-none absolute inset-0"
                aria-hidden="true"
                data-testid="mem-hero-flowers"
              >
                <span className="absolute -top-3 -left-3 text-4xl drop-shadow-lg select-none">🌼</span>
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-3xl drop-shadow select-none">🌹</span>
                <span className="absolute -top-3 -right-3 text-4xl drop-shadow-lg select-none">🌸</span>
                <span className="absolute top-1/2 -left-4 -translate-y-1/2 text-3xl drop-shadow select-none">🌺</span>
                <span className="absolute top-1/2 -right-4 -translate-y-1/2 text-3xl drop-shadow select-none">🌻</span>
                <span className="absolute -bottom-3 -left-3 text-4xl drop-shadow-lg select-none">🌷</span>
                <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-3xl drop-shadow select-none">🕯️</span>
                <span className="absolute -bottom-3 -right-3 text-4xl drop-shadow-lg select-none">🌼</span>
                {m.decorations.flag && (
                  <span
                    className="absolute top-2 left-2 text-2xl drop-shadow select-none"
                    title={m.decorations.flag}
                  >
                    🇮🇳
                  </span>
                )}
              </div>
            )}
          </div>
          <button onClick={toggleFollow} data-testid="mem-follow-btn" className={`mt-4 w-full ${m.is_following ? "wofy-btn-ghost" : "wofy-btn-primary"} justify-center`}>
            {m.is_following ? <><BellOff className="w-4 h-4" /> Following · {m.follower_count} </> : <><Bell className="w-4 h-4" /> Follow & get updates</>}
          </button>
          {m.is_following && user && (
            <AnniversaryOptIn slug={m.slug || m.id} fullName={m.full_name} />
          )}
          {/* Weather on this day — historical weather from the death date at the memorial's location */}
          {m.death_date && (
            <div className="mt-3">
              <WeatherOnThisDay
                date={m.death_date}
                lat={m.location?.lat ?? m.lat}
                lon={m.location?.lon ?? m.lng}
                location={
                  m.location?.lat ? null : [
                    m.death_place,
                    m.location?.city,
                    m.location?.state,
                    m.location?.country,
                  ].filter(Boolean).join(", ") || null
                }
                label="Weather on the day of passing"
              />
            </div>
          )}
          <button
            onClick={() => setShareOpen(true)}
            data-testid="mem-share-btn"
            className="mt-2 w-full wofy-btn-ghost justify-center"
            title="Create a shareable tribute card"
          >
            <Share2 className="w-4 h-4" /> Share this memorial
          </button>
          {/* Public "flag / contact admin" — anyone can send a note */}
          <button
            onClick={() => setContactAdminOpen(true)}
            data-testid="mem-contact-admin-btn"
            className="mt-2 w-full wofy-btn-ghost justify-center"
            title="Send the admins a private message about this memorial (privacy concern, correction, additional info…)"
          >
            <MessageCircle className="w-4 h-4" /> Contact us about this memorial
          </button>
          {/* Owner/Admin only — design a formal announcement card */}
          {(user && (user.id === m.submitter_user_id || user.is_admin)) && (
            <>
              <button
                onClick={() => setAnnouncementOpen(true)}
                data-testid="mem-announcement-btn"
                className="mt-2 w-full wofy-btn-ghost justify-center"
                title="Design a printable death announcement card"
              >
                <Palette className="w-4 h-4" /> Design announcement card
              </button>
              <button
                onClick={async () => {
                  try {
                    const { data } = await http.post(`/memorials/${m.id}/ai-announcement-text`);
                    if (data?.text) {
                      // Copy to clipboard for the family to paste anywhere
                      try { await navigator.clipboard.writeText(data.text); } catch { /* noop */ }
                      toast.success("AI announcement text ready — copied to clipboard.");
                      await load();
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "AI generation failed");
                  }
                }}
                data-testid="mem-ai-announcement-text-btn"
                className="mt-2 w-full wofy-btn-ghost justify-center"
                title="Generate a respectful announcement paragraph with AI"
              >
                <Sparkles className="w-4 h-4" /> AI announcement text
              </button>
              {/* AI Memorial Card (Nano Banana + PIL hybrid) */}
              <button
                onClick={async () => {
                  toast.info("Generating AI memorial card — this can take up to a minute…");
                  try {
                    const { data } = await http.post(`/memorials/${m.slug || m.id}/ai-memorial-card`, { style: "sunset_pine" });
                    if (data?.url) {
                      toast.success("AI memorial card generated");
                      await load();
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "AI card generation failed");
                  }
                }}
                data-testid="mem-ai-memorial-card-btn"
                className="mt-2 w-full wofy-btn-ghost justify-center"
                title="Blend the portrait onto a sunset-pine memorial background with name, dates and quote"
              >
                <Wand2 className="w-4 h-4" /> Generate AI memorial card
              </button>
            </>
          )}
          {/* Family Tree CTA */}
          <Link
            to={m.family_tree_slug ? `/family-trees/${m.family_tree_slug}` : "/family-trees/new"}
            className="mt-2 w-full wofy-btn-ghost justify-center"
            data-testid="mem-family-tree-cta"
          >
            <TreePine className="w-4 h-4" /> {m.family_tree_slug ? "View Family Tree" : "Create Your Family Tree"}
          </Link>
          {/* Encourage visitors to explore + start their own tree */}
          <div className="mt-3 rounded-xl p-3 border" style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, #F5F3FF 0%, #FFFBEB 100%)" }} data-testid="mem-family-tree-callout">
            <div className="flex items-start gap-2 mb-2">
              <TreePine className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#4338CA" }} />
              <div className="text-xs">
                {m.family_tree_slug ? (
                  <>Explore <b>{m.family_name || m.full_name?.split(" ").slice(-1)[0] || "this"} Family Tree</b> to see how {m.preferred_name || m.full_name?.split(" ")[0] || "they"} fits into the wider family.</>
                ) : (
                  <>Every life is a branch on a bigger story. <b>Start a family tree</b> to keep their memory in place, forever.</>
                )}
              </div>
            </div>
            <Link
              to="/family-trees/new"
              className="text-xs font-medium inline-flex items-center gap-1 hover:underline"
              style={{ color: "#4338CA" }}
              data-testid="mem-start-tree-cta"
            >
              <UserPlus className="w-3 h-3" /> Join or create your family tree →
            </Link>
          </div>
          {/* Print/Save announcement card — visible to everyone if a card exists */}
          {(m.announcement_cards || []).length > 0 && (
            <Link
              to={`/celebrate/${m.slug || m.id}/announcement-print`}
              className="mt-2 w-full wofy-btn-ghost justify-center"
              data-testid="mem-print-announcement-btn"
              title="Open a print-ready view · A5 / 5×7 · save as PDF or JPG"
            >
              <Printer className="w-4 h-4" /> Print / Save card
            </Link>
          )}
          {/* Owner/admin — regenerate the announcement card (after portrait upload) */}
          {(user && (user.id === m.submitter_user_id || user.is_admin)) && (m.announcement_cards || []).length > 0 && (
            <button
              onClick={async () => {
                try {
                  const { data } = await http.post(`/memorials/${m.slug || m.id}/announcement-card/regenerate`);
                  toast.success(`Regenerated · ${data.template} · ${data.palette}`);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Regeneration failed (LLM budget?)");
                }
              }}
              className="mt-2 w-full wofy-btn-ghost justify-center"
              data-testid="mem-regenerate-card-btn"
              title="Re-run the AI card generator with the current portrait + last chosen template"
            >
              <RefreshCcw className="w-4 h-4" /> Regenerate with new portrait
            </button>
          )}
          {/* Virtual tributes widget */}
          <div className="mt-3">
            <TributeWidget slug={m.slug || m.id} user={user} />
          </div>
          {/* Celebrate a Life services (request-a-service) */}
          <div className="mt-3">
            <MemorialServicesPanel memorialKey={m.slug || m.id} user={user} />
          </div>
          {/* Traditions guide — matches the memorial's declared religion,
              deep-links to the corresponding section of the master funeral
              article. Non-invasive card in the left sidebar; if no religion
              is set on the memorial, it still renders and links to the
              full guide so families can browse. */}
          <div className="mt-3">
            <RelatedTraditionCard religion={m.religion} />
          </div>
          {/* Admin-only — Memorial Verification Badge toggles */}
          {user?.is_admin && (
            <MemorialBadgeAdmin
              memorial={m}
              onUpdated={(patch) => setM((prev) => (prev ? { ...prev, ...patch } : prev))}
            />
          )}
          <p className="mt-2 text-[11px] font-mono text-center" style={{ color: "var(--wofy-text-soft)" }}>Following adds this memorial to your private profile and sends you anniversary reminders + updates.</p>
        </div>

        <div className="md:col-span-2">
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>IN LOVING MEMORY</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">{m.full_name}</h1>
          {m.known_as && <div className="mt-1 text-lg italic" style={{ color: "var(--wofy-text-soft)" }}>Known as {m.known_as}</div>}
          {(m.verification_badges?.length > 0) && (
            <MemorialBadges
              badges={m.verification_badges}
              size="md"
              className="mt-3"
              testidPrefix="memorial-detail-badge"
            />
          )}
          {years && <div className="mt-3 font-mono text-sm">{years}{m.occupation ? ` · ${m.occupation}` : ""}{m.nationality ? ` · ${m.nationality}` : ""}</div>}
          {m.quote && <blockquote className="mt-5 pl-4 border-l-4 italic" style={{ borderColor: "var(--wofy-primary)" }}>"{m.quote}"</blockquote>}
          {m.short_bio && <p className="mt-5 text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>{m.short_bio}</p>}
          {/* Tribute hero strip — one-tap candle/rose/carnation/bouquet/dove/prayer */}
          <TributeHeroStrip
            slug={m.slug || m.id}
            initialCounts={m.tribute_counts}
            memorialName={m.full_name}
            memorialYears={years}
          />

          {/* External source attribution (imported obituaries) */}
          {m.source_url && (
            <a
              href={m.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-start gap-2 rounded-xl border p-3 text-sm hover:shadow-md transition"
              style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, #FEF3C7 0%, #FFFFFF 60%)" }}
              data-testid="mem-source-attribution"
            >
              <Newspaper className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#B45309" }} />
              <div className="min-w-0">
                <div className="font-medium">
                  Read the full obituary at {m.source_attribution || "the original source"} →
                </div>
                <div className="text-xs opacity-70 mt-0.5 truncate">
                  This memorial links back to the original publisher — the full body text stays on their site.
                </div>
              </div>
            </a>
          )}
          {(m.wikipedia_url || (m.external_sources && m.external_sources.length > 0)) && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="mem-hero-sources">
              {(m.external_sources && m.external_sources.length > 0 ? m.external_sources : [{label:"Wikipedia", url: m.wikipedia_url}]).map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mem-hero-source-${i}`}
                  className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors hover:bg-[color:var(--wofy-surface)]"
                  style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-primary)" }}
                >
                  {s.label || "Source"} <span aria-hidden>↗</span>
                </a>
              ))}
            </div>
          )}

          {/* One-click family-tree linker. Only shown to signed-in
              users so a click reliably lands the memorial into "their"
              tree — auto-creating one if they don't have one yet, then
              minting every relative in family_details across. */}
          <FamilyTreeAutoLink m={m} user={user} onLinked={(payload) => {
            // Merge the returned tree link back into the local state so
            // the button flips to "View in Family Tree" without a page
            // reload. Local update only — the /api/memorials/{slug} GET
            // will pick it up on next mount.
            setM((prev) => prev ? {
              ...prev,
              family_tree_slug: payload?.tree?.slug || prev.family_tree_slug,
              family_tree_id:   payload?.tree?.id   || prev.family_tree_id,
            } : prev);
          }} />
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-10 flex flex-wrap gap-1 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        {[
          { v: "about", label: "About", icon: Users },
          { v: "obituary", label: "Obituary", icon: Newspaper },
          { v: "biography", label: "Biography", icon: Award },
          { v: "timeline", label: "Life Timeline", icon: Calendar },
          { v: "family", label: "Family", icon: Users },
          { v: "gallery", label: "Gallery", icon: Flower },
          { v: "videos", label: "Videos", icon: Video },
          { v: "funeral", label: "Funeral", icon: Church },
          { v: "guestbook", label: `Guestbook (${byKind("guestbook").length})`, icon: MessageCircle },
          { v: "memories", label: `Memories (${byKind("memory").length})`, icon: Heart },
          { v: "eulogies", label: `Eulogies (${byKind("eulogy").length})`, icon: Award },
          { v: "charities", label: "Charity Legacy", icon: DollarSign },
          { v: "genealogy", label: "Genealogy", icon: TreePine },
          { v: "news", label: "News", icon: Newspaper },
        ].map((t) => {
          const Ic = t.icon;
          return (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`mem-tab-${t.v}`}
                    className={`px-3 py-2.5 -mb-px text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors ${tab === t.v ? "" : "border-transparent hover:opacity-80"}`}
                    style={tab === t.v ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : { color: "var(--wofy-text-soft)" }}>
              <Ic className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {tab === "about" && <AboutPanel m={m} />}
        {tab === "obituary" && <RichBlock title="Obituary" body={m.obituary} />}
        {tab === "biography" && <BiographyPanel m={m} />}
        {tab === "timeline" && <TimelinePanel timeline={m.timeline || []} />}
        {tab === "family" && <FamilyPanel relationships={m.relationships || []} />}
        {tab === "gallery" && <GalleryPanel gallery={m.gallery || []} />}
        {tab === "videos" && <VideosPanel videos={m.videos || []} />}
        {tab === "funeral" && <FuneralPanel m={m} />}
        {tab === "charities" && <CharitiesPanel charities={m.charities || []} />}
        {tab === "genealogy" && <GenealogyPanel memorialKey={m.slug || m.id} />}
        {tab === "news" && <NewsPanel news={m.news || []} />}
        {["guestbook", "memories", "eulogies"].includes(tab) && (
          <div id="leave-a-memory" className="scroll-mt-24">
            <ContribPanel
              kind={tab === "guestbook" ? "guestbook" : tab === "memories" ? "memory" : "eulogy"}
              items={byKind(tab === "guestbook" ? "guestbook" : tab === "memories" ? "memory" : "eulogy")}
              newContrib={newContrib}
              setNewContrib={setNewContrib}
              onSubmit={submitContrib}
              onReact={react}
              user={user}
              memorialKey={m.slug || m.id}
            />
          </div>
        )}
      </div>

      {/* Stripe-powered memorial services: candle, flowers, donation, photo book, livestream, postcards */}
      {SUPPORT_PANEL_ENABLED && <MemorialSupportPanel memorial={m} />}

      {/* Guestbook Wall — scrollable grid of note-authored tributes */}
      <GuestbookWall slug={m.slug || m.id} />

      {/* "More people we're remembering from {city/state/country}" */}
      <RelatedMemorialsStrip memorial={m} />

      {shareOpen && <ShareCardModal memorial={m} onClose={() => setShareOpen(false)} />}
      {contactAdminOpen && <MemorialContactAdminModal memorial={m} onClose={() => setContactAdminOpen(false)} />}
      {announcementOpen && <AnnouncementCardModal memorial={m} onClose={() => setAnnouncementOpen(false)} onSaved={load} />}
    </div>
  );
}

function ShareCardModal({ memorial, onClose }) {
  const [state, setState] = useState({ loading: true, error: null, image: null, cached: false });
  const [copied, setCopied] = useState(false);

  // Always share the canonical production URL so previews resolve identically.
  const shareUrl = `https://socialpulse.com/celebrate/${memorial.slug}`;

  const _absoluteImage = (v) => {
    if (!v) return null;
    if (v.startsWith("data:") || v.startsWith("http")) return v;
    // Streaming URL from backend — resolve against current API host so <img> works in preview.
    return `${process.env.REACT_APP_BACKEND_URL || ""}${v}`;
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.post(`/memorials/${memorial.slug || memorial.id}/share-card`);
        const img = _absoluteImage(data.url || data.image);
        setState({ loading: false, error: null, image: img, cached: !!data.cached });
      } catch (e) {
        setState({ loading: false, error: e?.response?.data?.detail || "Could not generate the tribute card.", image: null, cached: false });
      }
    })();
  }, [memorial.id, memorial.slug]);

  const regenerate = async () => {
    setState({ loading: true, error: null, image: null, cached: false });
    try {
      const { data } = await http.post(`/memorials/${memorial.slug || memorial.id}/share-card?force=true`);
      const img = _absoluteImage(data.url || data.image);
      setState({ loading: false, error: null, image: img, cached: false });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || "Regeneration failed.", image: null, cached: false });
    }
  };

  const download = async () => {
    if (!state.image) return;
    // Both blob and data URLs — normalize to a Blob for consistent download.
    try {
      const res = await fetch(state.image);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      const safeName = (memorial.full_name || "memorial").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      const ext = blob.type.includes("png") ? "png" : "jpg";
      a.download = `${safeName}-tribute.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
    } catch { toast.error("Download failed"); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Could not copy link"); }
  };

  const shareToWhatsApp = () => {
    const text = `Remembering ${memorial.full_name} — ${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener");
  };

  const nativeShare = async () => {
    if (!navigator.share) return copyLink();
    try {
      // If we can turn the base64 into a File, offer image share too.
      if (state.image && navigator.canShare) {
        const blob = await (await fetch(state.image)).blob();
        const file = new File([blob], `${memorial.full_name}-tribute.png`, { type: blob.type });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `Remembering ${memorial.full_name}`, text: `In loving memory of ${memorial.full_name}`, url: shareUrl });
          return;
        }
      }
      await navigator.share({ title: `Remembering ${memorial.full_name}`, text: `In loving memory of ${memorial.full_name}`, url: shareUrl });
    } catch { /* user cancelled */ }
  };

  return (
    <div
      data-testid="share-card-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(20,33,61,0.28)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
        style={{ background: "var(--wofy-bg)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--wofy-border)" }}>
          <div>
            <div className="overline">SHARE A TRIBUTE</div>
            <div className="font-display text-lg mt-0.5">Remember {memorial.full_name}</div>
          </div>
          <button data-testid="share-modal-close" onClick={onClose} className="p-2 rounded-full hover:bg-[color:var(--wofy-surface)]"><XIcon className="w-4 h-4" /></button>
        </div>

        <div className="p-5">
          {/* Card preview */}
          <div className="aspect-square w-full rounded-xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
            {state.loading ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-sm gap-3" style={{ color: "var(--wofy-text-soft)" }}>
                <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: "var(--wofy-primary)", borderTopColor: "transparent" }} />
                Painting a tribute card…
                <span className="text-[11px] opacity-70">~20-40 seconds on first generation</span>
              </div>
            ) : state.error ? (
              <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center text-sm" style={{ color: "var(--wofy-text-soft)" }}>
                <span>{state.error}</span>
                <button onClick={regenerate} className="wofy-btn-ghost mt-4 text-xs">Try again</button>
              </div>
            ) : (
              <img data-testid="share-card-image" src={state.image} alt={`Tribute card for ${memorial.full_name}`} className="w-full h-full object-cover" />
            )}
          </div>

          {/* Actions */}
          <div className="mt-5 grid grid-cols-2 gap-2">
            <button data-testid="share-download" onClick={download} disabled={!state.image} className="wofy-btn-primary justify-center text-sm disabled:opacity-40">
              <Download className="w-4 h-4" /> Download
            </button>
            <button data-testid="share-native" onClick={nativeShare} disabled={!state.image} className="wofy-btn-ghost justify-center text-sm disabled:opacity-40">
              <Share2 className="w-4 h-4" /> Share…
            </button>
            <button data-testid="share-whatsapp" onClick={shareToWhatsApp} className="wofy-btn-ghost justify-center text-sm">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </button>
            <button data-testid="share-facebook" onClick={shareToFacebook} className="wofy-btn-ghost justify-center text-sm">
              <Facebook className="w-4 h-4" /> Facebook
            </button>
          </div>

          <button data-testid="share-copy-link" onClick={copyLink} className="mt-2 w-full wofy-btn-ghost justify-center text-sm">
            <Copy className="w-4 h-4" /> {copied ? "Link copied!" : "Copy memorial link"}
          </button>

          {state.image && !state.loading && (
            <button data-testid="share-regenerate" onClick={regenerate} className="mt-4 w-full text-[11px] font-mono uppercase tracking-wider opacity-60 hover:opacity-100">
              Regenerate card
            </button>
          )}
          <p className="mt-3 text-[11px] italic text-center" style={{ color: "var(--wofy-text-soft)" }}>
            Cards are generated with AI assistance (Nano Banana). Photos of your loved one are used only to render the tribute — never for any other purpose.
          </p>
        </div>
      </div>
    </div>
  );
}

function AboutPanel({ m }) {
  const rows = [
    ["Full name", m.full_name],
    ["Known as", m.known_as],
    ["Nicknames", (m.nicknames || []).join(", ")],
    ["Birth", [m.birth_date, m.birth_place].filter(Boolean).join(" · ")],
    ["Death", [m.death_date, m.death_place].filter(Boolean).join(" · ")],
    ["Gender", m.gender],
    ["Nationality", m.nationality],
    ["Citizenship", m.citizenship],
    ["Occupation", m.occupation],
    ["Profession", m.profession],
    ["Education", m.education],
    ["Military service", m.military_service],
    ["Religion", m.religion],
    ["Languages", (m.languages || []).join(", ")],
    ["Notable for", m.notable_for],
  ].filter(([, v]) => v && String(v).trim());
  const awards = m.awards || [];
  const sources = m.external_sources || [];
  const wikiUrl = m.wikipedia_url || (sources.find((s) => /wikipedia\.org/i.test(s.url || ""))?.url) || null;
  return (
    <div className="space-y-4">
      <div className="wofy-card">
        <h2 className="font-display text-2xl mb-4">Life at a glance</h2>
        <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="grid grid-cols-3 gap-2">
              <dt className="col-span-1 text-[11px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>{k}</dt>
              <dd className="col-span-2">{v}</dd>
            </div>
          ))}
        </dl>
      </div>

      {awards.length > 0 && (
        <div className="wofy-card" data-testid="mem-awards">
          <h2 className="font-display text-2xl mb-3">Awards & recognition</h2>
          <ul className="list-disc pl-5 space-y-1.5 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            {awards.map((a, i) => (<li key={i}>{a}</li>))}
          </ul>
        </div>
      )}

      {(wikiUrl || sources.length > 0) && (
        <div className="wofy-card" data-testid="mem-external-sources">
          <h2 className="font-display text-2xl mb-3">Learn more</h2>
          <p className="text-sm mb-4" style={{ color: "var(--wofy-text-soft)" }}>
            Verified references and further reading about this life.
          </p>
          <ul className="space-y-2 text-sm">
            {sources.length > 0 ? (
              sources.map((s, i) => (
                <li key={i}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mem-source-${i}`}
                    className="inline-flex items-center gap-1.5 underline"
                    style={{ color: "var(--wofy-primary)" }}
                  >
                    {s.label || s.url} <span aria-hidden>↗</span>
                  </a>
                </li>
              ))
            ) : (
              <li>
                <a
                  href={wikiUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mem-source-wikipedia"
                  className="inline-flex items-center gap-1.5 underline"
                  style={{ color: "var(--wofy-primary)" }}
                >
                  Wikipedia article <span aria-hidden>↗</span>
                </a>
              </li>
            )}
          </ul>
          <p className="mt-4 text-[11px] italic" style={{ color: "var(--wofy-text-soft)" }}>
            Wikipedia content is licensed under CC BY-SA. Social Pulse provides an editorial summary — please visit the source for the complete article.
          </p>
        </div>
      )}
    </div>
  );
}

function RichBlock({ title, body }) {
  if (!body) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>{title} not yet added.</div>;
  return (
    <div className="wofy-card">
      <h2 className="font-display text-2xl mb-4">{title}</h2>
      <div className="prose prose-sm max-w-none">
        <TranslatableText text={body} testid={`memorial-${title.toLowerCase().replace(/\s+/g, "-")}`} />
      </div>
    </div>
  );
}

function BiographyPanel({ m }) {
  const bio = m.ai_biography;
  if (!bio) {
    return (
      <>
        {m.biography ? (
          <div className="wofy-card mb-4">
            <div className="overline mb-3">FAMILY BIOGRAPHY</div>
            <div className="prose prose-sm max-w-none">
              <TranslatableText text={m.biography} testid="memorial-family-biography" />
            </div>
          </div>
        ) : null}
        <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          {m.biography
            ? "A long-form AI-woven biography for this memorial has not been generated yet. A moderator can trigger it from the admin panel."
            : "Biography not yet added. When the family shares more details or an admin runs the AI biographer, this space will fill with their story."}
        </div>
      </>
    );
  }
  return (
    <div data-testid="ai-biography">
      {m.biography ? (
        <details className="wofy-card mb-4 group">
          <summary className="cursor-pointer text-sm font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            Family biography (original) — click to expand
          </summary>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap leading-relaxed mt-4">{m.biography}</div>
        </details>
      ) : null}

      <article>
        <div className="overline">A LIFE IN FULL · AI-WOVEN BIOGRAPHY</div>
        <h2 className="font-display text-3xl mt-2 mb-4">{m.full_name}</h2>

        {bio.epitaph && (
          <div className="mb-6 py-3 px-5 rounded-lg font-display italic text-lg" style={{ background: "var(--wofy-surface)", borderLeft: "4px solid var(--wofy-primary)" }}>
            “{bio.epitaph}”
          </div>
        )}

        {bio.lead && (
          <p className="text-lg leading-relaxed first-letter:font-display first-letter:text-5xl first-letter:mr-2 first-letter:float-left first-letter:leading-none" style={{ color: "var(--wofy-text)" }}>
            {bio.lead}
          </p>
        )}

        <div className="space-y-8 mt-8">
          {bio.chapters?.map((c, i) => (
            <section key={i}>
              <h3 className="font-display text-2xl mb-3">{c.heading}</h3>
              <div className="space-y-3 text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
                {c.paragraphs?.map((p, j) => (<p key={j}>{p}</p>))}
              </div>
            </section>
          ))}
        </div>

        {bio.closing && (
          <p className="mt-10 text-base italic leading-relaxed" style={{ color: "var(--wofy-text)" }}>
            {bio.closing}
          </p>
        )}

        <p className="mt-8 text-[11px] italic" style={{ color: "var(--wofy-text-soft)" }}>
          This biography ({bio.word_count || 0} words) was woven from the facts the family shared, with AI assistance. It is offered with care — please contact the family or a moderator to suggest corrections.
        </p>
      </article>
    </div>
  );
}

function TimelinePanel({ timeline }) {
  if (!timeline.length) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>No timeline events yet.</div>;
  const sorted = [...timeline].sort((a, b) => (a.year || 0) - (b.year || 0));
  return (
    <div className="wofy-card">
      <h2 className="font-display text-2xl mb-6">Life Timeline</h2>
      <ol className="relative border-l-2 pl-6 space-y-6" style={{ borderColor: "var(--wofy-primary)" }}>
        {sorted.map((e, i) => (
          <li key={i} className="relative">
            <span className="absolute -left-[33px] top-1 w-4 h-4 rounded-full" style={{ background: "var(--wofy-primary)" }} />
            <div className="font-mono text-xs uppercase" style={{ color: "var(--wofy-primary)" }}>{e.year || e.date}</div>
            <div className="font-display text-lg">{e.title}</div>
            {e.location && <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}><MapPin className="w-3 h-3 inline mr-1" />{e.location}</div>}
            {e.description && <p className="text-sm mt-1">{e.description}</p>}
            {e.image && <img src={e.image} alt="" className="mt-3 rounded-lg max-h-64 object-cover" />}
          </li>
        ))}
      </ol>
    </div>
  );
}

function FamilyPanel({ relationships }) {
  if (!relationships.length) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>No family relationships added.</div>;
  const groups = relationships.reduce((acc, r) => { (acc[r.relation] = acc[r.relation] || []).push(r); return acc; }, {});
  return (
    <div className="wofy-card">
      <h2 className="font-display text-2xl mb-4">Family & connections</h2>
      <div className="space-y-4">
        {Object.entries(groups).map(([rel, people]) => (
          <div key={rel}>
            <div className="text-[11px] font-mono uppercase tracking-wider mb-2" style={{ color: "var(--wofy-text-soft)" }}>{rel.replace(/_/g, " ")}</div>
            <div className="flex flex-wrap gap-2">
              {people.map((p, i) => (
                p.memorial_id
                  ? <Link key={i} to={`/celebrate/${p.memorial_id}`} className="wofy-tag hover:underline">{p.name}</Link>
                  : <span key={i} className="wofy-tag">{p.name}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryPanel({ gallery }) {
  if (!gallery.length) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>No photos yet.</div>;
  const groups = gallery.reduce((acc, p) => { const b = p.bucket || "other"; (acc[b] = acc[b] || []).push(p); return acc; }, {});
  return (
    <div className="space-y-8">
      {Object.entries(groups).map(([bucket, photos]) => (
        <div key={bucket}>
          <div className="text-[11px] font-mono uppercase tracking-wider mb-3" style={{ color: "var(--wofy-primary)" }}>{bucket.replace(/_/g, " ")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.map((p, i) => (
              <figure key={i} className="rounded-lg overflow-hidden">
                <img src={p.url} alt={p.caption || ""} className="w-full aspect-square object-cover" />
                {p.caption && <figcaption className="mt-1 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{p.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function VideosPanel({ videos }) {
  if (!videos.length) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>No videos yet.</div>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {videos.map((v, i) => (
        <div key={i} className="wofy-card !p-0 overflow-hidden">
          <div className="aspect-video">
            <iframe title={v.title || `video-${i}`} src={v.url} className="w-full h-full" allowFullScreen />
          </div>
          {v.title && <div className="p-3 text-sm">{v.title}</div>}
        </div>
      ))}
    </div>
  );
}

function FuneralPanel({ m }) {
  const f = m.funeral || {}; const c = m.cemetery || {};
  const empty = !Object.keys(f).length && !Object.keys(c).length;
  if (empty) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>No funeral / cemetery information provided.</div>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="wofy-card">
        <h3 className="font-display text-xl mb-3">Funeral</h3>
        <dl className="text-sm space-y-2">
          {Object.entries(f).filter(([, v]) => v).map(([k, v]) => (
            <div key={k}><span className="text-[11px] font-mono uppercase mr-2" style={{ color: "var(--wofy-text-soft)" }}>{k}:</span>{String(v)}</div>
          ))}
        </dl>
      </div>
      <div className="wofy-card">
        <h3 className="font-display text-xl mb-3">Cemetery</h3>
        <dl className="text-sm space-y-2">
          {Object.entries(c).filter(([k, v]) => v && k !== "gps").map(([k, v]) => (
            <div key={k}><span className="text-[11px] font-mono uppercase mr-2" style={{ color: "var(--wofy-text-soft)" }}>{k}:</span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function CharitiesPanel({ charities }) {
  if (!charities.length) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>No charity legacy set up yet.</div>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {charities.map((c, i) => (
        <a key={i} href={c.url} target="_blank" rel="noreferrer" className="wofy-card hover:shadow-md">
          <div className="font-display text-xl">{c.name}</div>
          {c.note && <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>{c.note}</p>}
          {c.url && <div className="mt-3 text-xs underline">Donate in memory →</div>}
        </a>
      ))}
    </div>
  );
}


/**
 * GenealogyPanel — auto-generated deep-search links for the major
 * genealogy / cemetery / obituary databases. Fetched from the backend
 * so URL formats can evolve without a frontend redeploy.
 */
function GenealogyPanel({ memorialKey }) {
  const [rows, setRows] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!memorialKey) return;
    (async () => {
      try {
        const { data } = await http.get(`/memorials/${memorialKey}/genealogy-links`);
        setRows(data.links || []);
        setCtx(data.context || null);
      } catch { /* silent — fallback is empty state */ }
      finally { setBusy(false); }
    })();
  }, [memorialKey]);

  if (busy) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading genealogy links…</div>;
  if (!rows.length) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>Add a birth or death year to see genealogy links.</div>;

  const grouped = rows.reduce((acc, r) => { (acc[r.category] ||= []).push(r); return acc; }, {});
  const CATEGORY_LABEL = {
    records:    { label: "Records archives",       hint: "Deep-search the world's largest genealogy databases" },
    cemetery:   { label: "Cemetery & headstones",  hint: "Locate the resting place and headstone photos" },
    obituaries: { label: "Newspapers & obituaries", hint: "Historical newspaper mentions and obituaries" },
    trees:      { label: "Community family trees",  hint: "See if others have already researched relatives" },
    military:   { label: "Military service",         hint: "Service records, medals, and unit histories" },
    biography:  { label: "Biographical references",  hint: "Encyclopedia and published references" },
  };

  return (
    <div className="space-y-6" data-testid="genealogy-panel">
      <div className="wofy-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-xl">Continue the search elsewhere</h3>
            <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              We've built direct deep-search links for {ctx?.full_name || "this person"} across the leading
              genealogy, cemetery, and obituary databases. Click any provider — the searches are pre-filled
              with the birth year, death year, and place you supplied.
            </p>
          </div>
          {ctx && (
            <div className="text-[11px] font-mono uppercase opacity-70 shrink-0 text-right">
              {ctx.birth_year && <div>Born {ctx.birth_year}</div>}
              {ctx.death_year && <div>Died {ctx.death_year}</div>}
            </div>
          )}
        </div>
      </div>

      {Object.entries(grouped).map(([cat, links]) => (
        <div key={cat} className="wofy-card" data-testid={`genealogy-category-${cat}`}>
          <div className="mb-3">
            <div className="font-display text-lg">{CATEGORY_LABEL[cat]?.label || cat}</div>
            {CATEGORY_LABEL[cat]?.hint && (
              <div className="text-xs opacity-70">{CATEGORY_LABEL[cat].hint}</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {links.map((l) => (
              <a
                key={l.provider}
                href={l.url}
                target="_blank"
                rel="noreferrer"
                title={l.tooltip}
                data-testid={`genealogy-link-${l.provider}`}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium hover:shadow-sm transition-shadow"
                style={{ borderColor: "var(--wofy-border)", background: "white" }}
              >
                {l.label} <span aria-hidden>↗</span>
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function NewsPanel({ news }) {
  if (!news.length) return <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>No news mentions yet.</div>;
  return (
    <ul className="space-y-3">
      {news.map((n, i) => (
        <li key={i} className="wofy-card">
          <a href={n.url} target="_blank" rel="noreferrer" className="font-display text-lg hover:underline">{n.title}</a>
          <div className="text-xs font-mono mt-1" style={{ color: "var(--wofy-text-soft)" }}>{n.source} · {n.date}</div>
        </li>
      ))}
    </ul>
  );}

function ContribPanel({ kind, items, newContrib, setNewContrib, onSubmit, onReact, memorialKey }) {
  const label = kind === "guestbook" ? "Sign the guestbook" : kind === "memory" ? "Share a memory" : "Post a eulogy";
  const active = { ...newContrib, kind };
  return (
    <div className="space-y-5">
      <form onSubmit={(e) => { setNewContrib(active); onSubmit(e); }} className="wofy-card space-y-3" data-testid={`mem-contrib-form-${kind}`}>
        <h2 className="font-display text-2xl">{label}</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <input required placeholder="Your name" value={newContrib.author_name || ""} onChange={(e) => setNewContrib({ ...newContrib, author_name: e.target.value })} className="rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          <input required type="email" placeholder="Your email" value={newContrib.author_email || ""} onChange={(e) => setNewContrib({ ...newContrib, author_email: e.target.value })} className="rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
        </div>
        {memorialKey && (
          <AiMessageAssist
            memorialKey={memorialKey}
            kind={kind}
            onDraft={(text) => setNewContrib({ ...newContrib, body: text, kind })}
          />
        )}
        <SmartTextInput
          value={newContrib.body}
          onChange={(v) => setNewContrib({ ...newContrib, body: v, kind })}
          rows={5}
          maxLength={4000}
          placeholder={kind === "memory" ? "I remember when…" : kind === "eulogy" ? "In tribute…" : "Leave a message…"}
          testid={`mem-contrib-body-${kind}`}
        />
        <div className="flex justify-end"><button type="submit" data-testid={`mem-contrib-submit-${kind}`} className="wofy-btn-primary">Post {kind}</button></div>
        <p className="text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>Your post will be visible once approved by the family.</p>
      </form>

      {items.length === 0 ? (
        <div className="wofy-card text-sm" style={{ color: "var(--wofy-text-soft)" }}>No {kind} posts yet — be the first.</div>
      ) : items.map((c) => (
        <div key={c.id} className="wofy-card">
          <div className="flex items-center justify-between">
            <div className="font-display text-lg">{c.author_name}</div>
            <div className="text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>{new Date(c.created_at).toLocaleDateString()}</div>
          </div>
          <div className="mt-2 text-sm">
            <TranslatableText text={c.body} testid={`memorial-contrib-${c.id || ""}`} />
          </div>
          <div className="mt-3 flex gap-1">
            {Object.keys(REACTION_ICONS).map((r) => (
              <button key={r} onClick={() => onReact(c.id, r)} className="text-xs px-2 py-1 rounded-full hover:bg-[color:var(--wofy-surface)]">
                {REACTION_ICONS[r]} {c.reactions?.[r] || 0}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


/* ---------------- Announcement Card Designer (AI, multi-template) ---------------- */
function AnnouncementCardModal({ memorial, onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [existing, setExisting] = useState({ items: [], chosen_id: null });
  const [template, setTemplate] = useState("christian_classic");
  const [palette, setPalette] = useState("warm_cream_gold");
  const [generating, setGenerating] = useState(false);
  const [choosing, setChoosing] = useState(null);
  const [preview, setPreview] = useState(null); // { url, template, palette, id }

  const _absoluteImage = (v) => {
    if (!v) return null;
    if (v.startsWith("data:") || v.startsWith("http")) return v;
    return `${process.env.REACT_APP_BACKEND_URL || ""}${v}`;
  };

  useEffect(() => {
    http.get("/memorials/announcement-cards/config").then((r) => setCfg(r.data)).catch(() => {});
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const { data } = await http.get(`/memorials/${memorial.slug || memorial.id}/announcement-cards`);
      setExisting(data);
    } catch { /* noop */ }
  };

  const generate = async () => {
    setGenerating(true);
    setPreview(null);
    try {
      const { data } = await http.post(`/memorials/${memorial.slug || memorial.id}/announcement-card`, null, {
        params: { template, palette },
      });
      const url = _absoluteImage(data.url);
      setPreview({ ...data, url });
      await refresh();
      toast.success("Card generated — you can choose it as the primary card.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Card generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const chooseCard = async (cardId) => {
    setChoosing(cardId);
    try {
      await http.post(`/memorials/${memorial.slug || memorial.id}/announcement-card/choose/${cardId}`);
      toast.success("Announcement card chosen");
      await refresh();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to choose card");
    } finally {
      setChoosing(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid="mem-announcement-modal">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              <h3 className="font-display text-2xl">Announcement Card Designer</h3>
            </div>
            <p className="text-sm opacity-70 mt-1">Pick a template + colour palette and let AI create a printable, WhatsApp-ready death-announcement card.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1"><XIcon className="w-5 h-5" /></button>
        </div>

        {/* Template picker */}
        <div className="mb-4">
          <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Template style</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {(cfg?.templates || []).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTemplate(t.key)}
                className={`text-left p-3 rounded-xl border transition ${template === t.key ? "border-black bg-black text-white" : "hover:bg-black/5"}`}
                style={template !== t.key ? { borderColor: "var(--wofy-border)" } : undefined}
                data-testid={`mem-tmpl-${t.key}`}
              >
                <div className="font-medium text-sm">{t.label}</div>
                <div className={`text-[11px] mt-1 ${template === t.key ? "opacity-80" : "opacity-70"}`}>{t.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Palette picker */}
        <div className="mb-4">
          <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Colour palette</div>
          <div className="flex flex-wrap gap-2">
            {(cfg?.palettes || []).map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPalette(p.key)}
                className={`text-xs px-3 py-2 rounded-full border transition ${palette === p.key ? "bg-black text-white border-black" : "hover:bg-black/5"}`}
                style={palette !== p.key ? { borderColor: "var(--wofy-border)" } : undefined}
                data-testid={`mem-pal-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            className="wofy-btn-primary"
            data-testid="mem-generate-card"
          >
            {generating ? <><RefreshCcw className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate card</>}
          </button>
          <p className="text-[11px] opacity-70">Takes ~20 seconds. You can generate multiple variations and pick your favourite.</p>
        </div>

        {/* Latest preview */}
        {preview && (
          <div className="mb-6 wofy-card">
            <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Latest preview</div>
            <img src={preview.url} alt="Announcement card preview" className="w-full max-w-md mx-auto rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="mem-card-preview" />
          </div>
        )}

        {/* Gallery of saved variations */}
        {existing.items.length > 0 && (
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Saved variations ({existing.items.length})</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {existing.items.slice().reverse().map((c) => {
                const chosen = existing.chosen_id === c.id;
                const src = _absoluteImage(c.url);
                return (
                  <div key={c.id || c.generated_at} className={`rounded-xl border p-2 ${chosen ? "border-black" : ""}`} style={!chosen ? { borderColor: "var(--wofy-border)" } : undefined}>
                    {src && <img src={src} alt="" className="w-full aspect-[3/4] object-cover rounded-lg" />}
                    <div className="text-[10px] font-mono uppercase tracking-wider mt-2 opacity-70">{c.template} · {c.palette}</div>
                    <div className="mt-2 grid grid-cols-2 gap-1">
                      <button
                        type="button"
                        disabled={choosing === c.id || chosen}
                        onClick={() => c.id && chooseCard(c.id)}
                        className={`col-span-2 text-xs px-3 py-1.5 rounded-full ${chosen ? "bg-emerald-600 text-white" : "wofy-btn-ghost"}`}
                        data-testid={`mem-card-choose-${c.id || c.generated_at}`}
                      >
                        {chosen ? <><Check className="w-3 h-3" /> Chosen</> : "Choose this"}
                      </button>
                      {src && (
                        <>
                          <a
                            href={`/celebrate/${memorial.slug || memorial.id}/announcement-print`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 rounded-full wofy-btn-ghost text-center"
                            data-testid={`mem-card-print-${c.id}`}
                          >
                            <Printer className="w-3 h-3" /> Print
                          </a>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                if (c.storage_path && c.id) {
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/memorials/${memorial.slug || memorial.id}/announcement-card/${c.id}/download`;
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.rel = "noopener";
                                  a.download = `announcement-${memorial.slug}-${c.id}.jpg`;
                                  document.body.appendChild(a); a.click(); a.remove();
                                } else if (src.startsWith("data:")) {
                                  const a = document.createElement("a");
                                  a.href = src;
                                  a.download = `announcement-${memorial.slug || "card"}.jpg`;
                                  document.body.appendChild(a); a.click(); a.remove();
                                }
                                toast.success("Downloading…");
                              } catch { toast.error("Download failed"); }
                            }}
                            className="text-xs px-2 py-1 rounded-full wofy-btn-ghost"
                            data-testid={`mem-card-download-${c.id}`}
                          >
                            <Download className="w-3 h-3" /> Save
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
